<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationAPI from 'dashboard/api/conversations';
import conversationStatusesAPI from 'dashboard/api/conversationStatuses';
import serviceTemplatesAPI from 'dashboard/api/serviceTemplates';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
    Avatar,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
      workflowStatuses: [],
      serviceTemplates: [],
      attachedTemplates: [],
      selectedStatusId: '',
      selectedTemplateId: '',
      isLoadingWorkflow: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentAccount: 'getCurrentAccount',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    canIndividuallyAssign() {
      const permissions = this.currentAccount?.permissions || [];
      const inbox = this.currentInbox;
      if (inbox?.prevent_whatsapp_group_assignment && this.isWhatsappGroup) {
        return false;
      }
      return !(
        inbox?.assign_conversations_to_all_agents &&
        !permissions.includes('administrator') &&
        !permissions.includes('conversation_manage')
      );
    },
    isWhatsappGroup() {
      return this.currentChat?.meta?.sender?.identifier?.endsWith('@g.us');
    },
    currentInbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    isSupervisorInSharedInbox() {
      const permissions = this.currentAccount?.permissions || [];
      return (
        !!this.currentInbox?.assign_conversations_to_all_agents &&
        permissions.includes('conversation_manage') &&
        !permissions.includes('administrator')
      );
    },
    isSharedInbox() {
      return !!this.currentInbox?.assign_conversations_to_all_agents;
    },
    sharedDisplayAgent() {
      return this.assignedAgent || this.currentChat?.meta?.last_agent_reply;
    },
    canSelectAnyAgent() {
      return this.canIndividuallyAssign && !this.isSupervisorInSharedInbox;
    },
    canAssignTeam() {
      return (
        !this.currentInbox?.assign_conversations_to_all_agents &&
        !(
          this.currentInbox?.prevent_whatsapp_group_assignment &&
          this.isWhatsappGroup
        )
      );
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem.id;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.loadOperationalWorkflow();
      },
    },
  },
  methods: {
    async loadOperationalWorkflow() {
      this.isLoadingWorkflow = true;
      try {
        const [statuses, templates, attached] = await Promise.all([
          conversationStatusesAPI.get(),
          serviceTemplatesAPI.get(),
          ConversationAPI.getServiceTemplates(this.conversationId),
        ]);
        this.workflowStatuses = (statuses.data.payload || []).filter(
          status => status.active
        );
        this.serviceTemplates = (templates.data.payload || []).filter(
          template => template.active
        );
        this.attachedTemplates = attached.data.payload || [];
        this.selectedStatusId = this.currentChat?.conversation_status?.id || '';
      } catch {
        useAlert('Não foi possível carregar o fluxo de atendimento.');
      } finally {
        this.isLoadingWorkflow = false;
      }
    },
    async onOperationalStatusChange() {
      try {
        await ConversationAPI.updateOperationalStatus(
          this.conversationId,
          this.selectedStatusId || null
        );
        useAlert('Status de atendimento atualizado.');
      } catch {
        useAlert('Não foi possível atualizar o status de atendimento.');
      }
    },
    async attachTemplate() {
      if (!this.selectedTemplateId) return;
      try {
        await ConversationAPI.attachServiceTemplate(
          this.conversationId,
          this.selectedTemplateId
        );
        this.selectedTemplateId = '';
        await this.loadOperationalWorkflow();
        useAlert('Modelo vinculado à conversa.');
      } catch {
        useAlert('Este modelo já está vinculado ou não pôde ser adicionado.');
      }
    },
    async toggleChecklistItem(assignment, itemIndex) {
      const checklist = assignment.checklist.map((item, index) =>
        index === itemIndex ? { ...item, completed: !item.completed } : item
      );
      try {
        const { data } = await ConversationAPI.updateServiceTemplate(
          this.conversationId,
          assignment.id,
          checklist
        );
        const index = this.attachedTemplates.findIndex(
          item => item.id === assignment.id
        );
        this.attachedTemplates.splice(index, 1, data);
      } catch {
        useAlert('Não foi possível atualizar esta etapa.');
      }
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onReturnToSharedQueue() {
      this.assignedAgent = null;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority
        ? this.priorityOptions[0]
        : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div>
    <div v-if="canIndividuallyAssign || isSharedInbox">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="
              canIndividuallyAssign &&
              isSupervisorInSharedInbox &&
              !showSelfAssign
            "
            link
            xs
            icon="i-lucide-undo-2"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.RETURN_TO_SHARED_QUEUE')"
            @click="onReturnToSharedQueue"
          />
          <NextButton
            v-else-if="canIndividuallyAssign && showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        v-if="canSelectAnyAgent"
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
      <div
        v-if="isSharedInbox && !canSelectAnyAgent && sharedDisplayAgent"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12"
      >
        <Avatar
          :name="sharedDisplayAgent.name"
          :src="sharedDisplayAgent.thumbnail"
          :size="24"
        />
        <span class="truncate">{{ sharedDisplayAgent.name }}</span>
      </div>
    </div>
    <div v-if="canAssignTeam">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('OPERATIONAL_WORKFLOW.STATUS.TITLE')"
      />
      <select
        v-model="selectedStatusId"
        class="w-full h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
        :disabled="isLoadingWorkflow"
        @change="onOperationalStatusChange"
      >
        <option value="">{{ $t('OPERATIONAL_WORKFLOW.STATUS.NONE') }}</option>
        <option
          v-for="status in workflowStatuses"
          :key="status.id"
          :value="status.id"
        >
          {{ status.name }}
        </option>
      </select>
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('OPERATIONAL_WORKFLOW.TEMPLATE.TITLE')"
      />
      <div class="flex gap-2">
        <select
          v-model="selectedTemplateId"
          class="min-w-0 flex-1 h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
        >
          <option value="">
            {{ $t('OPERATIONAL_WORKFLOW.TEMPLATE.SELECT') }}
          </option>
          <option
            v-for="template in serviceTemplates"
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </select>
        <NextButton
          icon="i-lucide-plus"
          :disabled="!selectedTemplateId"
          @click="attachTemplate"
        />
      </div>
      <div
        v-for="assignment in attachedTemplates"
        :key="assignment.id"
        class="mt-3 rounded-lg border border-n-weak p-3"
      >
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ assignment.service_template.name }}
        </p>
        <label
          v-for="(item, index) in assignment.checklist"
          :key="`${assignment.id}-${index}`"
          class="flex items-start gap-2 py-1 text-sm text-n-slate-11"
        >
          <input
            type="checkbox"
            class="mt-0.5"
            :checked="item.completed"
            @change="toggleChecklistItem(assignment, index)"
          />
          <span :class="{ 'line-through opacity-60': item.completed }">{{
            item.title
          }}</span>
        </label>
      </div>
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>
